import { FetchBaseQueryError, createApi } from '@reduxjs/toolkit/query/react'

import { MAX_KUDOS, MAX_STREAK } from '~/math-vault/pages/MathVaultActivity/MathVaultActivity.const'
import { DB_COLLECTION } from '~/math-vault/constants/db.const'
import {
  LevelsSummaryModel,
  LevelSummaryModel,
  MathVaultEventModel,
  MathVaultUserDocModel,
  QuestionSummaryModel,
  SummaryModel,
  SummaryQueryParams,
} from '~/types/math-vault'
import { getFirebase } from '~/firebase'
import { apiPrivate } from '~/redux/utils/hom-api'
import {
  activityFailedUpdatedLevelsSummary,
  activityPassedUpdatedLevelsSummary,
} from '~/math-vault/utils/getUpdatedLevelSummary.utils'

import { rewardApi } from '~/redux/apis/reward-api'
import { updateUserMeta } from '~/redux/apis/user-meta-api'

import { mathVaultAnswerEventToFirebase } from '~/gamification/apis/gamification.api'
import { notificationApi } from '~/redux/apis/notification-api'
import { leaderboardApi } from '~/Leaderboard/apis/leaderboard-api'

export const mathVaultActivityApi = createApi({
  reducerPath: 'mathVaultActivityApi',
  baseQuery: apiPrivate,
  tagTypes: ['levelSummary'],
  endpoints: (build) => ({
    checkAnswer: build.mutation<
      unknown,
      {
        userId: number | null
        activityData: MathVaultEventModel
        levelsSummary: LevelsSummaryModel
        questionSummary: QuestionSummaryModel
        kudosEarned: number
        numberOfActivities: number
        streak: number
      }
    >({
      queryFn: async (event, { dispatch }) => {
        try {
          return {
            data: await mathVaultAnswerEventToFirebase(event.activityData).then(() => {
              dispatch(rewardApi.util.invalidateTags(['Rewards']))
              dispatch(notificationApi.util.invalidateTags(['notification']))
              dispatch(leaderboardApi.util.invalidateTags(['leaderboard']))

              if (event.activityData.chestCompleted) {
                dispatch(updateUserMeta.initiate({ lastMathVaultChest: event.activityData.chestId }))
              }
            }),
          }
        } catch (error) {
          return { error: error as FetchBaseQueryError }
        }
      },
      onQueryStarted: async (
        { userId, activityData, levelsSummary, questionSummary, kudosEarned, numberOfActivities, streak },
        { dispatch, queryFulfilled },
      ) => {
        if (!userId) {
          return
        }
        const { elevatorId, floorId, chestId, level, question, passed } = activityData
        const { numberOfTries, livesLeft, numberOfFailures, ...rest } = questionSummary
        let data = {}

        if (passed) {
          const isFirstAttempt = numberOfTries === 0

          data = {
            questionSummary: {
              ...questionSummary,
              passed: true,
            },
            levelsSummary: activityPassedUpdatedLevelsSummary(levelsSummary, level, question, numberOfActivities),
            kudosEarned: isFirstAttempt ? kudosEarned + 1 : kudosEarned,
            streak: isFirstAttempt ? streak + 1 : streak,
          }
        } else {
          const alreadyPassed = questionSummary.passed

          data = {
            levelsSummary: activityFailedUpdatedLevelsSummary(
              levelsSummary,
              level,
              question,
              numberOfActivities,
              alreadyPassed,
            ),
            questionSummary: {
              numberOfTries: numberOfTries + 1,
              livesLeft: livesLeft - 1,
              numberOfFailures: numberOfFailures + 1,
              ...rest,
            },
            kudosEarned,
            streak: alreadyPassed ? streak : 0,
          }
        }

        dispatch(
          mathVaultActivityApi.util.updateQueryData(
            'getSummary',
            {
              id: String(userId),
              elevator: elevatorId,
              floor: floorId,
              chest: chestId,
              level: String(level),
              question: String(question),
            },
            (draft) => {
              Object.assign(draft, data)
            },
          ),
        )

        try {
          await queryFulfilled
          if (passed) {
            dispatch(mathVaultActivityApi.util.invalidateTags(['levelSummary']))
          }
        } catch (error) {
          dispatch(mathVaultActivityApi.util.invalidateTags(['levelSummary']))
        }
      },
    }),
    getSummary: build.query<SummaryModel, SummaryQueryParams>({
      queryFn: async ({ id, elevator, floor, chest, level, question }) => {
        try {
          const basePath = [DB_COLLECTION.MATH_VAULT, id].join('/')
          const chestPath = [
            basePath,
            DB_COLLECTION.STATE,
            elevator,
            DB_COLLECTION.FLOORS,
            floor,
            DB_COLLECTION.CHESTS,
            chest,
          ].join('/')
          const { doc, fireStore, collection, getDoc, getDocs } = await getFirebase()
          const userRef = doc(fireStore, basePath)
          const chestDataRef = collection(fireStore, chestPath, DB_COLLECTION.LEVELS)
          const questionDataRef = doc(
            fireStore,
            chestPath,
            DB_COLLECTION.LEVELS,
            level.toString(),
            DB_COLLECTION.QUESTIONS,
            question.toString(),
          )

          const { kudosEarned = 0, streak = 0 } = (await getDoc(userRef))?.data() || ({} as MathVaultUserDocModel)

          const {
            questionNo = question,
            passed = false,
            firstPassOnTryNo = -1,
            numberOfTries = 0,
            numberOfFailures = 0,
          } = (await getDoc(questionDataRef))?.data() || ({} as unknown as QuestionSummaryModel)

          const levelsSnapshot = await getDocs(chestDataRef)
          const levelsSummary: { [key: string]: LevelSummaryModel } = {}
          levelsSnapshot.docs.forEach((_doc) => {
            levelsSummary[_doc.id] = { ..._doc.data(), levelNumber: _doc.id } as LevelSummaryModel
          })

          const summary: SummaryModel = {
            levelsSummary,
            questionSummary: {
              questionNo,
              passed,
              firstPassOnTryNo,
              numberOfFailures,
              numberOfTries,
              livesLeft: numberOfFailures === 0 ? 2 : 1,
            },
            kudosEarned: kudosEarned % MAX_KUDOS,
            streak: streak % MAX_STREAK,
          }
          return { data: summary }
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          }
        }
      },
      providesTags: ['levelSummary'],
    }),
  }),
})

export const { useCheckAnswerMutation, useGetSummaryQuery } = mathVaultActivityApi
